import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { GreenLiquidGlassButton } from '@/shared/ui/GreenLiquidGlassButton';
import { subscriptionApi } from '@/entities/subscription/api';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

type CorrectionType = 'writeoff' | 'correction';
type Direction = 'increase' | 'decrease';

export function ManualCorrection() {
  const { id, membershipId } = useParams<{ id: string; membershipId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();
  const [type, setType] = useState<CorrectionType>('writeoff');
  const [direction, setDirection] = useState<Direction>('decrease');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');

  const membersQ = useQuery({
    queryKey: ['subscription-members', id],
    queryFn: () => subscriptionApi.getMembers(id!),
    enabled: !!id,
  });

  const member = useMemo(
    () => membersQ.data?.items.find((item) => item.membership_id === membershipId),
    [membersQ.data?.items, membershipId],
  );

  const parsedAmount = Number(amount.replace(',', '.'));
  const canSubmit = Boolean(member && Number.isFinite(parsedAmount) && parsedAmount > 0);

  const mutation = useMutation({
    mutationFn: () =>
      subscriptionApi.manualCorrection(id!, {
        participant_id: member!.user_id,
        type,
        amount: parsedAmount,
        increase: type === 'correction' && direction === 'increase',
        comment: comment || undefined,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription-members', id] });
      await qc.invalidateQueries({ queryKey: ['subscription-operations', id] });
      navigate(`/subscriptions/${id}/members/${membershipId}`);
    },
  });

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{t('management.correction.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <div className="flex flex-col gap-2">
          {(['writeoff', 'correction'] as CorrectionType[]).map((value) => (
            <button
              key={value}
              onClick={() => setType(value)}
              className={cn(
                'flex items-center gap-3 rounded-[12px] px-4 py-3 text-[14px] bg-white/50 border',
                type === value ? 'border-2 border-brand-green bg-brand-green/10' : 'border-white/60',
              )}
            >
              <span className={cn('w-[17px] h-[17px] rounded-full border-2', type === value ? 'border-brand-green bg-brand-green' : 'border-[#b9c4bb]')} />
              {value === 'writeoff' ? t('management.correction.typeWriteoff') : t('management.correction.typeCorrection')}
            </button>
          ))}
        </div>

        {type === 'correction' && (
          <div className="flex flex-col gap-2">
            {(['decrease', 'increase'] as Direction[]).map((value) => (
              <button
                key={value}
                onClick={() => setDirection(value)}
                className={cn(
                  'flex items-center gap-3 rounded-[12px] px-4 py-3 text-[14px] bg-white/50 border',
                  direction === value ? 'border-2 border-brand-green bg-brand-green/10' : 'border-white/60',
                )}
              >
                <span className={cn('w-[17px] h-[17px] rounded-full border-2', direction === value ? 'border-brand-green bg-brand-green' : 'border-[#b9c4bb]')} />
                {value === 'decrease' ? t('management.correction.directionDecrease') : t('management.correction.directionIncrease')}
              </button>
            ))}
          </div>
        )}

        <label className="flex flex-col gap-2">
          <span className="text-[14px] font-semibold text-text-secondary">{t('management.correction.amount')}</span>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="rounded-[14px] px-4 py-4 text-[24px] font-mono font-semibold bg-white/55 border border-white/65 outline-none focus:border-brand-green"
            placeholder="100"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[14px] font-semibold text-text-secondary">{t('management.correction.comment')}</span>
          <input
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            className="rounded-[12px] px-4 py-3 text-[15px] bg-white/55 border border-white/60"
          />
        </label>

        <div className="mt-auto">
          {mutation.isError && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('management.correction.error')}</p>
          )}
          <GreenLiquidGlassButton
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {t('management.correction.submit')}
          </GreenLiquidGlassButton>
        </div>
      </div>
    </Page>
  );
}
